/**
 * Hook do saldo bancario real (Task #132 — substitui mock R$ 4,4M).
 *
 * Mesmo padrao dos hooks existentes (useLastSync / usePosicaoAberta):
 * cache module-level + refetch periodico. TTL 1 minuto — saldo Omie e
 * atualizado pelo cron diario 07:15 BRT, mas a Mesa tem direito de ver
 * mudancas de freshness logo apos o cron rodar.
 *
 * Em caso de falha (rede/auth/5xx) devolve null em data + flag de erro.
 * Card consumidor decide entre renderizar valor ultimo conhecido ou
 * mock fallback com badge "AGUARDANDO INTEGRACAO".
 */
import { useEffect, useState } from "react";
import { getSaldoV2 } from "../api/client";
import type { SaldoV2Response } from "../api/types";
import { startVisiblePolling } from "./visiblePolling";

const REFETCH_MS = 180 * 1000; // #75: 60s->180s (fonte muda via cron diario + sync manual)

interface CacheEntry {
  expiresAt: number;
  promise: Promise<SaldoV2Response>;
}

let cache: CacheEntry | null = null;

export function invalidateSaldoV2Cache(): void {
  cache = null;
}

export interface UseSaldoV2State {
  data: SaldoV2Response | null;
  isLoading: boolean;
  error: Error | null;
}

export function useSaldoV2(): UseSaldoV2State {
  const [state, setState] = useState<UseSaldoV2State>({
    data: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    let cancel = false;

    function load() {
      const now = Date.now();
      if (!cache || cache.expiresAt < now) {
        cache = { expiresAt: now + REFETCH_MS, promise: getSaldoV2() };
      }
      cache.promise
        .then((data) => {
          if (cancel) return;
          setState({ data, isLoading: false, error: null });
        })
        .catch((err: Error) => {
          if (cancel) return;
          // Invalida o cache em erro pra proxima rodada tentar de novo
          // (caso contrario a UI ficaria presa em erro ate o intervalo).
          cache = null;
          setState({ data: null, isLoading: false, error: err });
        });
    }

    load();
    // #75: tick skipa com a aba em background; volta visivel/focus -> 1 load.
    const stop = startVisiblePolling(load, REFETCH_MS);
    return () => {
      cancel = true;
      stop();
    };
  }, []);

  return state;
}
